import { HttpContext, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { siQuoteUrls } from '../../../environments/environment';
import { EstimateSearchResponse } from "../../models/EstimateSearchResponse";
import { EstimateSearchDropDownDataResponse } from "../../models/EstimateSearchDropDownDataResponse";
import { EstimateSearchRequest } from "../../models/EstimateSearchRequest";
import { EstimateSearchDropDownDataRequest } from "../../models/EstimateSearchDropDownDataRequest";
import { EstimateSaveRequest, EstimateSaveResponse, InitializeEstimatePropertiesDropDownDataRequest, InitializeEstimatePropertiesDropDownDataResponse, 
  MetaDataRequest, MetaDataResponse, MinimumProjectStandardMarginRangeResponse, MinimumProjectStandardMarginRequestBase, OpenEstimateRequest, 
  OpenEstimateResponse, ProposalExportRequest, ProposalExportResponse, RatesRequestModel, RatesResponseModel, SQWebProjectContainer, 
  UnlinkProposalRequest } from "../../models/dtos";
import { JsonDeserializer } from "../../services/utils/json-deserializer";
import { BaseHttpService, StreamEvent } from "./base-http.service";
import { withLocalErrorHandling, withSilentBackgroundRequest } from "../../core/errors/http-context-tokens";

export type ProposalExportEvent = StreamEvent<ProposalExportResponse>;

@Injectable({
  providedIn: 'root'
})
export class OpenEstimateApiService {

  constructor(private readonly baseHttp: BaseHttpService) { }

  searchEstimateService(requestBody: EstimateSearchRequest): Observable<EstimateSearchResponse> {
    return this.baseHttp.post<EstimateSearchResponse>(`${siQuoteUrls.estimateOperations}/SearchEstimates`, requestBody);
  }

  getEstimateSearchDropDownData(requestBody: EstimateSearchDropDownDataRequest): Observable<EstimateSearchDropDownDataResponse> {
    return this.baseHttp.post<EstimateSearchDropDownDataResponse>(`${siQuoteUrls.estimateOperations}/GetEstimateSearchDropDownData`, requestBody);
  }

  //TODO: to be removed
  getProjectByEstimateId(estimateId: string, userId: string): Observable<SQWebProjectContainer> {
    const params = new HttpParams()
      .set('estimateUniqueID', estimateId)
      .set('userID', userId);

    return this.baseHttp.get<SQWebProjectContainer>(`${siQuoteUrls.projectOperation}/OpenProjectById`, params)
      .pipe(
        map(response => {
          return JsonDeserializer.deserialize<SQWebProjectContainer>(response);
        })
      );
  }

  saveProject(request: EstimateSaveRequest): Observable<EstimateSaveResponse> {
    const context: HttpContext = withLocalErrorHandling();
    return this.baseHttp.post<EstimateSaveResponse>(`${siQuoteUrls.estimateOperations}/Save`, request, { context });
  }

  getAllRates(requestBody: RatesRequestModel): Observable<RatesResponseModel> {
    return this.baseHttp.post<RatesResponseModel>(`${siQuoteUrls.metaData}/GetAllRates`, requestBody);
  }

  openEstimate(payload: OpenEstimateRequest): Observable<OpenEstimateResponse> {
    return this.baseHttp.post<OpenEstimateResponse>(`${siQuoteUrls.estimateOperations}/OpenEstimate`, payload);
  }

  getMetaData(request: MetaDataRequest): Observable<MetaDataResponse> {
    return this.baseHttp.post<MetaDataResponse>(`${siQuoteUrls.metaData}/GetMetaData`, request);
  }

  getMinimumProjectStandardMarginRange(request: MinimumProjectStandardMarginRequestBase): Observable<MinimumProjectStandardMarginRangeResponse> {
    return this.baseHttp.post<MinimumProjectStandardMarginRangeResponse>(`${siQuoteUrls.metaData}/GetMinimumProjectStandardMarginRange`, request);
  }

  initializePropertiesDropDowns(requestBody: InitializeEstimatePropertiesDropDownDataRequest): Observable<InitializeEstimatePropertiesDropDownDataResponse> {
    return this.baseHttp.post<InitializeEstimatePropertiesDropDownDataResponse>(`${siQuoteUrls.metaData}/InitializePropertiesDropDowns`, requestBody);
  }

  exportToProposal(requestBody: ProposalExportRequest): Observable<ProposalExportEvent> {
    const context: HttpContext = withSilentBackgroundRequest();
    return this.baseHttp.postTypedStream<ProposalExportResponse>(`${siQuoteUrls.proposal}/ProposalExport`, requestBody, { context });
  }

  unlinkProposal(requestBody: UnlinkProposalRequest): Observable<void> {
    return this.baseHttp.post<void>(`${siQuoteUrls.proposal}/UnlinkProposal`, requestBody);
  }
}
